import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, message } from 'antd';
import { assignBankRelationship, updateBankRelationship } from '@/api/custom';
import { useAppSelector } from '@/store/hooks';
import { formatAccountType } from '@/util/account-type-format';
import { selectPlaidAccounts } from '@/store/features/plaid/plaid-selectors';
import {
  BankLinkReuseSource,
  BankLinkWallet,
  FUNDING_BANK_SLOT_EMPTIED_MESSAGE,
  FundingChip,
  findReuseSource,
  fundingBankChangedMessage,
  fundingBankInitials,
  fundingBankLogoSrc,
  fundingBankRowSubtitle,
  isFundingBankSlotEmptiedError,
  walletHasActiveFundingBank,
} from '@/util/bank-link';
import {
  getAccountFundingPlaidId,
  getDepositEligiblePlaidAccounts,
} from './deposit-account-selection';

type FundingBankReplacePanelProps = {
  chip: FundingChip;
  wallet?: BankLinkWallet;
  pendingSources: readonly BankLinkReuseSource[];
  /** Apex's allowed funding banks; when given, only these are offered. */
  allowedFundingIds?: readonly string[];
  onClose: () => void;
  onUpdated: () => void;
};

const REPLACE_FAILED_MESSAGE = "Couldn't change your funding bank. Try again.";

const FundingBankReplacePanel: React.FC<FundingBankReplacePanelProps> = ({
  chip,
  wallet,
  pendingSources,
  allowedFundingIds,
  onClose,
  onUpdated,
}) => {
  const plaidAccounts = useAppSelector(selectPlaidAccounts);
  const eligibleAccounts = useMemo(() => {
    const deposit = getDepositEligiblePlaidAccounts(plaidAccounts);
    if (!allowedFundingIds) {
      return deposit;
    }
    const allowed = new Set(allowedFundingIds);
    return deposit.filter((account) => allowed.has(getAccountFundingPlaidId(account)));
  }, [plaidAccounts, allowedFundingIds]);
  const hasActiveFundingBank = wallet
    ? walletHasActiveFundingBank(wallet)
    : chip.status === 'connected' || chip.status === 'pending';
  const currentPlaidId = (wallet?.current_link?.plaid_id || chip.plaidId || '').trim();
  const [selectedPlaidId, setSelectedPlaidId] = useState(currentPlaidId);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Keep any error: a reload after a failed change moves the current bank too.
  useEffect(() => {
    setSelectedPlaidId(currentPlaidId);
  }, [currentPlaidId]);

  useEffect(() => {
    const currentIndex = eligibleAccounts.findIndex(
      (account) => getAccountFundingPlaidId(account) === currentPlaidId,
    );
    setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
  }, [currentPlaidId, eligibleAccounts]);

  // account_name is Apex's resource path; the account type reads better.
  const accountName = formatAccountType(wallet?.goal) || 'this account';
  const selectedAccount = eligibleAccounts.find(
    (account) => getAccountFundingPlaidId(account) === selectedPlaidId,
  );
  const pendingSource = selectedPlaidId
    ? findReuseSource(pendingSources, selectedPlaidId)
    : undefined;
  const selectedIsCurrent = Boolean(selectedPlaidId) && selectedPlaidId === currentPlaidId;
  const primaryBlocked =
    submitting ||
    eligibleAccounts.length === 0 ||
    !selectedPlaidId ||
    Boolean(pendingSource) ||
    (hasActiveFundingBank && selectedIsCurrent);

  const handleSubmit = async () => {
    if (primaryBlocked || submittingRef.current || !selectedAccount) {
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      if (hasActiveFundingBank) {
        await updateBankRelationship(chip.accountId, selectedPlaidId);
      } else {
        await assignBankRelationship(chip.accountId, selectedPlaidId);
      }
      message.success(fundingBankChangedMessage(selectedAccount));
      onUpdated();
      onClose();
    } catch (submitError) {
      console.error('Failed to change funding bank', submitError);
      const submitMessage = submitError instanceof Error ? submitError.message : '';
      if (isFundingBankSlotEmptiedError(submitMessage)) {
        // The old bank is gone; reload so the wallet shows no bank and the
        // next try connects instead of replacing.
        setError(FUNDING_BANK_SLOT_EMPTIED_MESSAGE);
        onUpdated();
      } else {
        setError(REPLACE_FAILED_MESSAGE);
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const moveFocus = (nextIndex: number) => {
    if (eligibleAccounts.length === 0) {
      return;
    }
    const wrapped =
      (nextIndex + eligibleAccounts.length) % eligibleAccounts.length;
    setFocusedIndex(wrapped);
    optionRefs.current[wrapped]?.focus();
  };

  return (
    <div className="funding-bank-replace-panel" onClick={(event) => event.stopPropagation()}>
      <div className="funding-bank-replace-title">
        {hasActiveFundingBank ? 'Replace funding bank' : 'Add funding bank'}
      </div>
      <p className="funding-bank-replace-copy">
        Future deposits and withdrawals for {accountName} will use the bank you pick.
      </p>
      {eligibleAccounts.length === 0 ? (
        <p className="funding-bank-replace-hint">
          Link a checking or savings account first. You can do that from Wealth.
        </p>
      ) : (
        <div
          role="listbox"
          aria-label="Linked bank accounts"
          className="funding-bank-replace-list"
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              moveFocus(focusedIndex + 1);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              moveFocus(focusedIndex - 1);
            } else if (event.key === 'Enter') {
              event.preventDefault();
              const focused = eligibleAccounts[focusedIndex];
              if (focused) {
                setSelectedPlaidId(getAccountFundingPlaidId(focused));
                // A new pick starts over; the last failure no longer applies.
                setError(null);
              }
            }
          }}
        >
          {eligibleAccounts.map((account, index) => {
            const plaidId = getAccountFundingPlaidId(account);
            const isCurrent = Boolean(currentPlaidId) && plaidId === currentPlaidId;
            const isSelected = plaidId === selectedPlaidId;
            const blockedSource = findReuseSource(pendingSources, plaidId);
            const bankName = account.institution_name?.trim() || 'Linked bank';
            const imageSrc = fundingBankLogoSrc(account.institution_logo);
            const subtitle = fundingBankRowSubtitle(account);
            return (
              <button
                key={plaidId}
                type="button"
                role="option"
                aria-selected={isSelected}
                disabled={Boolean(blockedSource) || submitting}
                tabIndex={index === focusedIndex ? 0 : -1}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                className={`funding-bank-replace-option${isSelected ? ' is-selected' : ''}`}
                onClick={() => {
                  setSelectedPlaidId(plaidId);
                  setFocusedIndex(index);
                  setError(null);
                }}
              >
                {imageSrc ? (
                  <img src={imageSrc} alt="" className="funding-bank-replace-logo" />
                ) : (
                  <span className="funding-bank-replace-initials" aria-hidden>
                    {fundingBankInitials(bankName)}
                  </span>
                )}
                <span className="funding-bank-replace-option-copy">
                  <span className="funding-bank-replace-bank">{bankName}</span>
                  <span className="funding-bank-replace-details">{subtitle}</span>
                  {blockedSource ? (
                    <span className="funding-bank-replace-blocked">
                      Apex is still approving this bank on {blockedSource.source_account_name}.
                    </span>
                  ) : null}
                </span>
                {isCurrent ? <span className="funding-bank-replace-current">Current</span> : null}
              </button>
            );
          })}
        </div>
      )}
      {error ? (
        <p className="funding-bank-replace-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="funding-bank-replace-actions">
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button type="primary" loading={submitting} disabled={primaryBlocked} onClick={() => void handleSubmit()}>
          {hasActiveFundingBank ? 'Replace' : 'Connect'}
        </Button>
      </div>
    </div>
  );
};

export default FundingBankReplacePanel;
